/**
 * Command legitimacy.
 *
 * The hard problem in a 33-player public team is not drawing on a map, it is
 * getting anyone to follow the person drawing. Every competing tool makes the
 * person who opened the room the commander, which is exactly the arrangement
 * randoms ignore.
 *
 * So command here is held by consent, and the flow is tuned for the sixty
 * seconds you actually have in spawn:
 *
 *   - An empty chair can be CLAIMED instantly. No vote, no waiting. Most
 *     sessions never need more than this, because usually nobody is arguing.
 *   - A held chair can be CONTESTED. That opens a vote, and a vote is the only
 *     thing that can move command from one player to another.
 *   - A vote needs both a majority of those present and a floor of votes, so
 *     two people in a twenty-player session cannot quietly stage a coup.
 *   - Votes expire. A stalled vote must not leave the team leaderless.
 *   - If the commander disconnects, the chair opens again after a grace period
 *     so a crash does not cost the team its command structure.
 *
 * This module is pure: it takes state and returns decisions. All of it is
 * unit-tested, and none of it knows about Yjs or React.
 */

export type CommandState =
  | { readonly kind: "vacant" }
  | { readonly kind: "held"; readonly commanderId: string; readonly since: number }
  | {
      readonly kind: "contested";
      readonly commanderId: string | null;
      readonly challengerId: string;
      readonly openedAt: number;
      readonly closesAt: number;
    };

export interface Vote {
  readonly voterId: string;
  /** True for the challenger, false to keep things as they are. */
  readonly inFavour: boolean;
  readonly at: number;
}

/** How long a contest stays open before it resolves automatically. */
export const VOTE_DURATION_MS = 45_000;

/**
 * Grace period before a disconnected commander loses the chair. Long enough to
 * survive a map load or a crash to desktop, short enough that the team is not
 * stuck waiting.
 */
export const COMMANDER_GRACE_MS = 90_000;

/**
 * Minimum number of votes in favour, regardless of turnout. Prevents a pair of
 * players from taking command of a large session while everyone else is busy.
 */
export const MIN_VOTES_IN_FAVOUR = 3;

/**
 * Sessions smaller than this skip the floor and run on simple majority —
 * a five-player squad should not need three votes to replace a commander.
 */
export const SMALL_SESSION_THRESHOLD = 5;

export interface VoteTally {
  readonly inFavour: number;
  readonly against: number;
  readonly eligible: number;
  /** Votes in favour still needed to pass. Zero once the threshold is met. */
  readonly needed: number;
  readonly wouldPass: boolean;
}

/** Count votes and work out whether the challenge currently succeeds. */
export function tallyVotes(votes: readonly Vote[], eligibleCount: number): VoteTally {
  let inFavour = 0;
  let against = 0;
  const seen = new Set<string>();

  for (const vote of votes) {
    // One vote per player; a later vote replaces an earlier one upstream, but
    // guard here too so a malformed doc cannot stuff the ballot.
    if (seen.has(vote.voterId)) continue;
    seen.add(vote.voterId);
    if (vote.inFavour) inFavour++;
    else against++;
  }

  const threshold = requiredVotes(eligibleCount);
  return {
    inFavour,
    against,
    eligible: eligibleCount,
    needed: Math.max(0, threshold - inFavour),
    wouldPass: inFavour >= threshold,
  };
}

/** Votes in favour required for a challenge to succeed. */
export function requiredVotes(eligibleCount: number): number {
  if (eligibleCount <= 0) return 1;
  const majority = Math.floor(eligibleCount / 2) + 1;
  if (eligibleCount < SMALL_SESSION_THRESHOLD) return majority;
  return Math.max(majority, MIN_VOTES_IN_FAVOUR);
}

export type CommandAction =
  | { readonly type: "claim"; readonly playerId: string; readonly at: number }
  | { readonly type: "contest"; readonly playerId: string; readonly at: number }
  | { readonly type: "resign"; readonly playerId: string; readonly at: number }
  | { readonly type: "resolve"; readonly at: number }
  | { readonly type: "commanderLost"; readonly at: number };

export interface CommandContext {
  readonly state: CommandState;
  readonly votes: readonly Vote[];
  /** Players currently connected and therefore able to vote. */
  readonly eligibleCount: number;
}

export interface CommandOutcome {
  readonly state: CommandState;
  /** True when the caller should clear the ballot. */
  readonly clearVotes: boolean;
  /** Set when the action was refused, for surfacing in the UI. */
  readonly rejected?: string;
}

/**
 * Apply an action to the command state.
 *
 * Returns the next state rather than mutating, so the caller decides how to
 * write it. Refusals come back as `rejected` with the reason.
 */
export function applyCommandAction(
  context: CommandContext,
  action: CommandAction,
): CommandOutcome {
  const { state } = context;

  switch (action.type) {
    case "claim": {
      if (state.kind === "vacant") {
        return {
          state: { kind: "held", commanderId: action.playerId, since: action.at },
          clearVotes: true,
        };
      }
      if (state.kind === "held" && state.commanderId === action.playerId) {
        return { state, clearVotes: false };
      }
      return {
        state,
        clearVotes: false,
        rejected: "Command is already held. Contest it to call a vote.",
      };
    }

    case "contest": {
      if (state.kind === "vacant") {
        // Nothing to contest — treat it as a claim so an eager tap still works.
        return {
          state: { kind: "held", commanderId: action.playerId, since: action.at },
          clearVotes: true,
        };
      }
      if (state.kind === "contested") {
        return { state, clearVotes: false, rejected: "A vote is already running." };
      }
      if (state.commanderId === action.playerId) {
        return { state, clearVotes: false, rejected: "You already hold command." };
      }
      return {
        state: {
          kind: "contested",
          commanderId: state.commanderId,
          challengerId: action.playerId,
          openedAt: action.at,
          closesAt: action.at + VOTE_DURATION_MS,
        },
        clearVotes: true,
      };
    }

    case "resign": {
      if (state.kind === "held" && state.commanderId === action.playerId) {
        return { state: { kind: "vacant" }, clearVotes: true };
      }
      if (state.kind === "contested" && state.commanderId === action.playerId) {
        // Standing down mid-vote hands it straight to the challenger: the team
        // wanted a change and the incumbent agrees, so do not make them wait.
        return {
          state: { kind: "held", commanderId: state.challengerId, since: action.at },
          clearVotes: true,
        };
      }
      return { state, clearVotes: false, rejected: "You do not hold command." };
    }

    case "commanderLost": {
      if (state.kind === "held") {
        return { state: { kind: "vacant" }, clearVotes: true };
      }
      if (state.kind === "contested") {
        return {
          state: { kind: "held", commanderId: state.challengerId, since: action.at },
          clearVotes: true,
        };
      }
      return { state, clearVotes: false };
    }

    case "resolve": {
      if (state.kind !== "contested") return { state, clearVotes: false };
      if (action.at < state.closesAt) {
        // Let a decisive result land early rather than making everyone wait out
        // the clock — but only when the outcome can no longer change.
        const tally = tallyVotes(context.votes, context.eligibleCount);
        if (!tally.wouldPass) return { state, clearVotes: false };
      }

      const tally = tallyVotes(context.votes, context.eligibleCount);
      if (tally.wouldPass) {
        return {
          state: { kind: "held", commanderId: state.challengerId, since: action.at },
          clearVotes: true,
        };
      }
      return {
        state:
          state.commanderId === null
            ? { kind: "vacant" }
            : { kind: "held", commanderId: state.commanderId, since: action.at },
        clearVotes: true,
      };
    }

    default:
      return { state, clearVotes: false };
  }
}

/** Whether a contest has run past its deadline and should be resolved. */
export function isVoteExpired(state: CommandState, now: number): boolean {
  return state.kind === "contested" && now >= state.closesAt;
}

/** Milliseconds left on an open vote, or null when no vote is running. */
export function voteTimeRemaining(state: CommandState, now: number): number | null {
  if (state.kind !== "contested") return null;
  return Math.max(0, state.closesAt - now);
}

/** The player currently holding command, if any. */
export function currentCommanderId(state: CommandState): string | null {
  if (state.kind === "held") return state.commanderId;
  if (state.kind === "contested") return state.commanderId;
  return null;
}

export function isCommander(state: CommandState, playerId: string): boolean {
  return currentCommanderId(state) === playerId;
}
